import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from '../Navbar';
import Sidebar from '../Sidebar';
import Footer from '../Footer';
import { FooterSpacer } from '../Footer';
import IP_URL from '../connect';
import { useSelector } from 'react-redux';
import { selectUser } from '../../Redux/currentUserSlice';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const FETCH_PAGE_SIZE = 100; // how many records to request per network call while loading the full dataset
const DISPLAY_PAGE_SIZE = 100; // how many rows to show per page in the table (client-side)

const Main = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', sans-serif;
`;

const Content = styled.div`
  display: flex;
  flex: 1;
`;

const TableContainer = styled.div`
  flex: 1;
  padding: 20px;
  overflow-x: auto;
  transition: width 0.3s ease;
  width: ${({ sidebarOpen }) => (sidebarOpen ? '85vw' : '100vw')};

  @media (max-width: 768px) {
    width: 100%;
    padding: 10px;
  }
`;

const PageHeading = styled.h2`
  font-size: 20px;
  color: #1e293b;
  margin: 0 0 14px 2px;
  font-weight: 600;
`;

const Input = styled.input`
  padding: 8px;
  margin: 5px;
  border-radius: 5px;
  border: 1px solid #ccc;
  outline: none;
  transition: border 0.2s ease;

  &:focus {
    border-color: #1976d2;
  }
    @media(max-width: 600px) {
      width:80%;
      margin:3px;
      padding:4px;
    }
`;
const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  input {
    width: 150px;
    padding: 8px 8px 8px 30px; // padding-left for icon
    border-radius: 5px;
    border: 1px solid #ccc;
    outline: none;
    transition: border 0.2s ease;

    &:focus {
      border-color: #1976d2;
    }

    @media (max-width: 600px) {
      width: 100%;
      font-size: 12px;
      padding: 8px 8px 8px 22px;
    }
  }

  svg {
    position: absolute;
    left: 8px;
    color: #999;
    font-size: 18px;

    @media (max-width: 600px) {
      left: 4px;
    }
  }
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
  overflow-x:auto;
  border: 1px solid #eef1f5;
`;

const Table = styled.table`
  border-collapse: collapse;
  background-color: #fdfdfd;
  width: 100%;

  th, td {
    padding: 12px 15px;
    border: 1px solid #e9edf2;
    text-align: center;
    white-space: nowrap;
  }

  th {
    background-color: #1976d2;
    color: white;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  td.row-number {
    color: #94a3b8;
    font-weight: 600;
    background: #fafcff;
  }

  tbody tr {
    transition: background 0.15s ease;
    cursor: pointer;
  }

  tbody tr:nth-child(odd) {
    background-color: #eef6fdff;
  }

  tbody tr:hover {
    background-color: #dbeeff;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    th, td {
      padding: 6px 8px;
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin: 15px 0;
  gap: 10px;

  span {
    min-width: 100px;
    text-align: center;
    color: #475569;
    font-weight: 500;
  }

  button {
    padding: 8px 16px;
    font-size: 14px;
    border: none;
    border-radius: 6px;
    background: #1976d2;
    color: white;
    cursor: pointer;
    transition: background 0.15s ease, opacity 0.15s ease;

    &:disabled { opacity: 0.5; cursor: not-allowed; }
    &:not(:disabled):hover { background: #135ba1; }

    @media (max-width: 600px) {
      padding: 6px 10px;
      font-size: 12px;
    }
  }
`;

/* ---- Toolbar, column picker, modals, editable cell ---- */

const fadeSlide = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeScale = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 12px;
  position: relative;
`;

const ToolbarButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${(p) => (p.danger ? '#dc3545' : '#1976d2')};
  color: white;
  border: none;
  padding: 9px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0,0,0,0.08);
  transition: background 0.15s ease, transform 0.1s ease;

  svg { font-size: 17px; }

  &:hover:not(:disabled) {
    background: ${(p) => (p.danger ? '#c62839' : '#135ba1')};
    transform: translateY(-1px);
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }

  @media (max-width: 600px) {
    font-size: 12px;
    padding: 7px 11px;
  }
`;

const ColumnPickerPanel = styled.div`
  position: absolute;
  top: 46px;
  right: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.14);
  padding: 14px 16px;
  z-index: 30;
  min-width: 230px;
  max-height: 320px;
  overflow-y: auto;
  animation: ${fadeSlide} 0.15s ease;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;
  cursor: pointer;
  input { cursor: pointer; }
`;

const PanelHeading = styled.div`
  font-weight: bold;
  font-size: 11px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 22px;
  width: 340px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 12px 36px rgba(0,0,0,0.25);
  animation: ${fadeScale} 0.15s ease;
  h3 { margin-top: 0; color: #1e293b; }
`;

const ModalField = styled.div`
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  label { font-size: 12px; color: #555; font-weight: 600; }
  input, select {
    padding: 9px;
    border-radius: 7px;
    border: 1px solid #ccc;
    outline: none;
    &:focus { border-color: #1976d2; }
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 18px;
`;

const EditableValue = styled.span`
  cursor: text;
  display: inline-block;
  min-width: 40px;
  padding: 2px 6px;
  border-radius: 4px;
  color: ${(p) => (p.$empty ? '#9aa4b2' : 'inherit')};

  &:hover {
    background: #eef6ff;
  }
`;

const EditInput = styled.input`
  padding: 4px 6px;
  border: 1px solid #1976d2;
  border-radius: 4px;
  width: 120px;
  font-size: inherit;
`;

// Turn camelCase / snake_case custom field keys into readable labels, e.g. "wspAccountNo" -> "Wsp Account No"
const prettifyKey = (key) =>
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

// Turn a typed label into a camelCase key, e.g. "WSP Account No" -> "wspAccountNo"
const labelToKey = (label) =>
  label
    .trim()
    .split(/\s+/)
    .map((word, i) => (i === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1).toLowerCase()))
    .join('');

const HARDCODED_COLUMNS = [
  { key: 'serialNumber', label: 'Serial Number', accessor: (d) => d.serialNumber, custom: false },
  { key: 'meterName', label: 'Meter Name', accessor: (d) => d.meterName, custom: false },
  { key: 'meterType', label: 'Meter Type', accessor: (d) => d.meterType, custom: false },
  { key: 'meterSize', label: 'Meter Size', accessor: (d) => d.meterSize, custom: false },
  { key: 'lastReading', label: 'Last Reading', accessor: null, custom: false }, // needs formatLocalTime, wired below
  { key: 'valveStatus', label: 'Valve Status', accessor: null, custom: false }, // needs color/label logic, wired below
  { key: 'user', label: 'User', accessor: null, custom: false }, // needs click-to-assign UI, wired below
];

const MeterData = () => {
const [searchMeterId, setSearchMeterId] = useState('');
const [searchSerialNo, setSearchSerialNo] = useState('');
const [searchMeterName, setSearchMeterName] = useState('');
const [searchMeterUser, setSearchMeterUser] = useState('');

// Full dataset for this org, loaded once (across all backend pages) so search
// can match meters regardless of which "page" they'd land on.
const [meterData, setMeterData] = useState([]);
const [sidebarOpen, setSidebarOpen] = useState(false);

// Client-side display pagination (separate from the network fetch pagination below)
const [page, setPage] = useState(1);

const [loading, setLoading] = useState(false);
const { token, uniqueKey } = useSelector(selectUser);
const navigate = useNavigate();

// --- dynamic columns state ---
const [visibleColumns, setVisibleColumns] = useState({}); // { [key]: boolean }
const [showColumnPicker, setShowColumnPicker] = useState(false);
const [showAddColumn, setShowAddColumn] = useState(false);
const [newColumnLabel, setNewColumnLabel] = useState('');
const [manualColumnKeys, setManualColumnKeys] = useState([]); // columns added via "+ Add Column" with no data yet

// --- per-custom-column search state ---
const [customColumnSearch, setCustomColumnSearch] = useState({}); // { [key]: searchTerm }
const setCustomSearchValue = (key, value) =>
  setCustomColumnSearch((prev) => ({ ...prev, [key]: value }));

// --- inline edit state ---
const [editingCell, setEditingCell] = useState(null); // { rowId, key }
const [editingValue, setEditingValue] = useState('');

// --- export picker state ---
const [showExportPicker, setShowExportPicker] = useState(false);
const [pendingExportFormat, setPendingExportFormat] = useState(null);
const [exportSelectedKeys, setExportSelectedKeys] = useState([]);

const toggleSidebar = () => setSidebarOpen((prev) => !prev);

// Load the ENTIRE dataset for this org once (paging through the backend
// FETCH_PAGE_SIZE at a time internally), so search/filtering below always
// operates over every meter — not just whichever page was last requested.
useEffect(() => {
  fetchAllMeterData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [uniqueKey]);

const fetchAllMeterData = async () => {
  setLoading(true);
  try {
    // First request tells us how many total pages exist
    const firstRes = await fetch(`${IP_URL}/meter/all-meter-data/${uniqueKey}?page=1&limit=${FETCH_PAGE_SIZE}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!firstRes.ok) throw new Error(`Error: ${firstRes.statusText}`);
    const firstData = await firstRes.json();

    let combined = firstData.data || [];
    const totalBackendPages = firstData.totalPages || 1;

    // Fetch any remaining backend pages in parallel and merge them in
    if (totalBackendPages > 1) {
      const remainingPageNumbers = Array.from({ length: totalBackendPages - 1 }, (_, i) => i + 2);
      const remainingResponses = await Promise.all(
        remainingPageNumbers.map((p) =>
          fetch(`${IP_URL}/meter/all-meter-data/${uniqueKey}?page=${p}&limit=${FETCH_PAGE_SIZE}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }).then((r) => r.json())
        )
      );
      remainingResponses.forEach((r) => {
        combined = combined.concat(r.data || []);
      });
    }

    setMeterData(combined);
    setPage(1);
    toast.success('Data loaded successfully');
  } catch (error) {
    console.error('Fetch Error:', error);
    toast.error('Failed to load meter data. Please try again.');
  } finally {
    setLoading(false);
  }
};

// Full set of meters matching every active search field, across the ENTIRE
// dataset (not just the current display page) — this is what fixes search
// missing meters that would otherwise sit on a different page.
const filteredData = meterData.filter((meter) => {
const idMatch = !searchMeterId || meter.meterId?.toLowerCase().includes(searchMeterId.toLowerCase());
const serialMatch = !searchSerialNo || meter.serialNumber?.toLowerCase().includes(searchSerialNo.toLowerCase());
const nameMatch = !searchMeterName || meter.meterName?.toLowerCase().includes(searchMeterName.toLowerCase());
const userMatch = !searchMeterUser || meter.assignedUserPhoneNumber?.toLowerCase().includes(searchMeterUser.toLowerCase());

// custom column search — every active custom search term must match
const customMatch = Object.entries(customColumnSearch).every(([key, term]) => {
  if (!term) return true;
  const value = meter.customFields?.[key];
  return value !== undefined && value !== null && String(value).toLowerCase().includes(term.toLowerCase());
});

return idMatch && serialMatch && nameMatch && userMatch && customMatch;
  });

// Reset to page 1 whenever any search term changes, so the user doesn't get
// stuck on a page number that no longer has any matching results.
useEffect(() => {
  setPage(1);
}, [searchMeterId, searchSerialNo, searchMeterName, searchMeterUser, customColumnSearch]);

// Client-side pagination over the FILTERED set
const totalPages = Math.max(1, Math.ceil(filteredData.length / DISPLAY_PAGE_SIZE));
const pagedData = filteredData.slice((page - 1) * DISPLAY_PAGE_SIZE, page * DISPLAY_PAGE_SIZE);

const navigateToMeterDetails = (id) => navigate(`/meter-details/${id}`);
const navigateToAddUser = (id, userPhoneNumber, meterName) =>
navigate(`/add-MeterUser/${id}/${userPhoneNumber}/${meterName}`);

const formatLocalTime = (isoString) => {
if (!isoString) return 'N/A';
const date = new Date(isoString);
return date.toLocaleString('en-GB', {
day: '2-digit', month: 'short', year: 'numeric',
hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

const valveLabel = (status) => ({ 0: 'Open', 1: 'Closed', 2: 'No Valve' }[status] || 'Unknown');
const valveColor = (status) =>
  status === 0 ? 'green' : status === 1 ? 'red' : status === 2 ? 'grey' : 'black';

// --- discover custom field keys present in the data (union across all rows) + any manually-added-but-empty columns ---
const customColumnKeys = useMemo(() => {
  const set = new Set(manualColumnKeys);
  meterData.forEach((d) => {
    if (d.customFields && typeof d.customFields === 'object') {
      Object.keys(d.customFields).forEach((k) => set.add(k));
    }
  });
  return Array.from(set);
}, [meterData, manualColumnKeys]);

// --- full column registry, hardcoded columns wired with real accessors + custom columns ---
const allColumns = useMemo(() => {
  const wiredHardcoded = HARDCODED_COLUMNS.map((c) => {
    if (c.key === 'lastReading') return { ...c, accessor: (d) => formatLocalTime(d.latestReading?.createdAt) };
    if (c.key === 'valveStatus') return { ...c, accessor: (d) => valveLabel(d.latestReading?.valveControlStatus) };
    if (c.key === 'user') return { ...c, accessor: (d) => d.assignedUserPhoneNumber || 'No User' };
    return c;
  });

  const customCols = customColumnKeys.map((key) => ({
    key,
    label: prettifyKey(key),
    accessor: (d) => (d.customFields && d.customFields[key] !== undefined && d.customFields[key] !== null && d.customFields[key] !== ''
      ? d.customFields[key]
      : ''),
    custom: true,
  }));

  return [...wiredHardcoded, ...customCols];
}, [customColumnKeys]);

// keep visibility map in sync as columns are discovered/added (default: visible)
useEffect(() => {
  setVisibleColumns((prev) => {
    const next = { ...prev };
    let changed = false;
    allColumns.forEach((c) => {
      if (!(c.key in next)) {
        next[c.key] = true;
        changed = true;
      }
    });
    return changed ? next : prev;
  });
}, [allColumns]);

const displayedColumns = useMemo(
  () => allColumns.filter((c) => visibleColumns[c.key]),
  [allColumns, visibleColumns]
);

const toggleColumnVisible = (key) => {
  setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
};

// --- add a brand-new custom column (definition only, no value yet) ---
const handleAddColumn = () => {
  const label = newColumnLabel.trim();
  if (!label) return toast.warn('Enter a column name');
  const key = labelToKey(label);
  if (allColumns.some((c) => c.key === key)) {
    toast.warn('A column with that name already exists');
    return;
  }
  setManualColumnKeys((prev) => [...prev, key]);
  setNewColumnLabel('');
  setShowAddColumn(false);
  toast.success(`Column "${label}" added — click a cell in that column to set a value`);
};

// --- save a custom field value on a specific meter ---
const saveCustomField = async (meterObjectId, key, value) => {
  try {
    const response = await fetch(`${IP_URL}/meter/custom-field/${meterObjectId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uniqueKey, key, value }),
    });
    if (!response.ok) throw new Error('Failed to save');

    // update local state so the table reflects the change immediately
    setMeterData((prev) =>
      prev.map((m) =>
        m._id === meterObjectId
          ? { ...m, customFields: { ...m.customFields, [key]: value } }
          : m
      )
    );
    toast.success('Saved');
  } catch (error) {
    console.error('Error saving custom field:', error);
    toast.error('Failed to save value');
  }
};

const startEditing = (rowId, key, currentValue) => {
  setEditingCell({ rowId, key });
  setEditingValue(currentValue ?? '');
};

const confirmEditing = () => {
  if (!editingCell) return;
  saveCustomField(editingCell.rowId, editingCell.key, editingValue);
  setEditingCell(null);
  setEditingValue('');
};

const cancelEditing = () => {
  setEditingCell(null);
  setEditingValue('');
};

// --- export flow — pick format, then pick columns, then generate ---
const openExportPicker = (format) => {
  setPendingExportFormat(format);
  setExportSelectedKeys(displayedColumns.map((c) => c.key));
  setShowExportPicker(true);
};

const toggleExportKey = (key) => {
  setExportSelectedKeys((prev) =>
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
  );
};

// Excel (and most spreadsheet apps) auto-detect numeric-looking CSV cells and strip
// leading zeros on open (e.g. "001" displays as "1"), even though the CSV file itself
// is correct. Wrapping such values as ="001" forces Excel to treat them as literal text.
const forceTextIfLeadingZero = (value) => {
  const str = value === null || value === undefined ? '' : String(value);
  if (/^0\d+$/.test(str)) {
    return `="${str}"`;
  }
  return str;
};

const runExportCSV = (cols) => {
  // Exports the full FILTERED set (every matching meter across all pages), not just
  // whatever page happens to be showing on screen.
  if (!filteredData.length) return toast.warn('No data to export');
  const formatted = filteredData.map((item) => {
    const row = {};
    cols.forEach((c) => { row[c.label] = forceTextIfLeadingZero(c.accessor(item)); });
    return row;
  });
  const csv = Papa.unparse(formatted);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `meter_data_${new Date().toISOString().slice(0, 10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  toast.success('CSV exported successfully');
};

const runExportPDF = (cols) => {
  if (!filteredData.length) return toast.warn('No data to export');
  const doc = new jsPDF('landscape');
  const pageWidth = doc.internal.pageSize.getWidth();

  const generatedAt = new Date().toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });

  // Title
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('Meter Data Report', pageWidth / 2, 16, { align: 'center' });

  // Subtitle line: generated date + record count
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(100);
  doc.text(`Generated on ${generatedAt}`, pageWidth / 2, 23, { align: 'center' });
  doc.text(`${filteredData.length} record${filteredData.length === 1 ? '' : 's'}`, pageWidth / 2, 28, { align: 'center' });
  doc.setTextColor(0);

  const headers = [cols.map((c) => c.label)];
  const rows = filteredData.map((item) => cols.map((c) => c.accessor(item)));
  doc.autoTable({
    head: headers,
    body: rows,
    startY: 34,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
    margin: { top: 20 },
  });

  doc.save(`meter_data_${new Date().toISOString().slice(0, 10)}.pdf`);
  toast.success('PDF exported successfully');
};

const confirmExport = () => {
  const cols = allColumns.filter((c) => exportSelectedKeys.includes(c.key));
  if (!cols.length) return toast.warn('Select at least one column to export');
  if (pendingExportFormat === 'csv') runExportCSV(cols);
  if (pendingExportFormat === 'pdf') runExportPDF(cols);
  setShowExportPicker(false);
  setPendingExportFormat(null);
};

return (
<Main>
<Navbar showMenuButton={true} toggleSidebar={toggleSidebar} />
<Content>
<Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

<TableContainer sidebarOpen={sidebarOpen}>
<PageHeading>Meters</PageHeading>
{loading && <p style={{ textAlign: 'center', fontWeight: 600 }}>Loading data...</p>}

<Toolbar>
  <ToolbarButton onClick={() => setShowAddColumn(true)}>
    <AddCircleOutlineIcon /> Add Column
  </ToolbarButton>

  <ToolbarButton onClick={() => setShowColumnPicker((v) => !v)}>
    <ViewColumnIcon /> Columns ({displayedColumns.length}/{allColumns.length})
  </ToolbarButton>
  {showColumnPicker && (
    <ColumnPickerPanel>
      <PanelHeading>Standard columns</PanelHeading>
      {allColumns.filter((c) => !c.custom).map((c) => (
        <CheckboxRow key={c.key}>
          <input type="checkbox" checked={!!visibleColumns[c.key]} onChange={() => toggleColumnVisible(c.key)} />
          {c.label}
        </CheckboxRow>
      ))}
      {allColumns.some((c) => c.custom) && (
        <>
          <PanelHeading style={{ marginTop: 10 }}>Custom columns</PanelHeading>
          {allColumns.filter((c) => c.custom).map((c) => (
            <CheckboxRow key={c.key}>
              <input type="checkbox" checked={!!visibleColumns[c.key]} onChange={() => toggleColumnVisible(c.key)} />
              {c.label}
            </CheckboxRow>
          ))}
        </>
      )}
    </ColumnPickerPanel>
  )}

  <ToolbarButton onClick={() => openExportPicker('csv')}>
    <InsertDriveFileIcon /> Export CSV
  </ToolbarButton>
  <ToolbarButton onClick={() => openExportPicker('pdf')}>
    <PictureAsPdfIcon /> Export PDF
  </ToolbarButton>
</Toolbar>

{!loading && (
<TableWrapper>
<Table>
<thead>
<tr>
<th></th> {/* # row number column has no search */}
{displayedColumns.map((c) => {
  if (c.key === 'serialNumber') return <th key="search-serial"><InputWrapper><SearchIcon /><Input type="text" placeholder="Serial No" value={searchSerialNo} onChange={(e) => setSearchSerialNo(e.target.value)} /></InputWrapper></th>;
  if (c.key === 'meterName') return <th key="search-name"><InputWrapper><SearchIcon /><Input type="text" placeholder="Meter Name" value={searchMeterName} onChange={(e) => setSearchMeterName(e.target.value)} /></InputWrapper></th>;
  if (c.key === 'user') return <th key="search-user"><InputWrapper><SearchIcon /><Input type="text" placeholder="User" value={searchMeterUser} onChange={(e) => setSearchMeterUser(e.target.value)} /></InputWrapper></th>;
  if (c.custom) {
    return (
      <th key={`search-${c.key}`}>
        <InputWrapper>
          <SearchIcon />
          <Input
            type="text"
            placeholder={c.label}
            value={customColumnSearch[c.key] || ''}
            onChange={(e) => setCustomSearchValue(c.key, e.target.value)}
          />
        </InputWrapper>
      </th>
    );
  }
  return <th key={`search-${c.key}`}></th>;
})}
</tr>
<tr>
<th>#</th>
{displayedColumns.map((c) => <th key={c.key}>{c.label}</th>)}
</tr>
</thead>
<tbody>
{pagedData.length > 0 ? (
                    pagedData.map((meter, index) => (
<tr key={meter._id || index} onClick={() => navigateToMeterDetails(meter.imei || meter.meterId)}>
<td className="row-number">{(page - 1) * DISPLAY_PAGE_SIZE + index + 1}</td>
{displayedColumns.map((c) => {
  if (c.key === 'valveStatus') {
    return (
      <td key={c.key} style={{ color: valveColor(meter.latestReading?.valveControlStatus) }}>
        {valveLabel(meter.latestReading?.valveControlStatus)}
      </td>
    );
  }
  if (c.key === 'user') {
    return (
      <td
        key={c.key}
        style={{ color: meter.assignedUserPhoneNumber ? '#007bff' : 'gray', cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          navigateToAddUser(meter.imei || meter.meterId, meter.assignedUserPhoneNumber, meter.meterName);
        }}
      >
        {meter.assignedUserPhoneNumber || 'No User'}
      </td>
    );
  }
  if (c.custom) {
    const isEditing = editingCell && editingCell.rowId === meter._id && editingCell.key === c.key;
    const rawValue = meter.customFields?.[c.key] ?? '';
    return (
      <td key={c.key} onClick={(e) => e.stopPropagation()}>
        {isEditing ? (
          <EditInput
            autoFocus
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onBlur={confirmEditing}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmEditing();
              if (e.key === 'Escape') cancelEditing();
            }}
          />
        ) : (
          <EditableValue $empty={!rawValue} onClick={() => startEditing(meter._id, c.key, rawValue)}>
            {rawValue || 'Click to add'}
          </EditableValue>
        )}
      </td>
    );
  }
  return <td key={c.key}>{c.accessor(meter)}</td>;
})}
</tr>
                    ))
                  ) : (
<tr><td colSpan={displayedColumns.length + 1 || 1} style={{ textAlign: 'center', padding: '10px' }}>No results found</td></tr>
                  )}
</tbody>
</Table>
</TableWrapper>
          )}

<PaginationContainer>
<button disabled={page === 1 || loading} onClick={() => setPage(p => p - 1)}>Previous</button>
<span>Page {page} of {totalPages} ({filteredData.length} meter{filteredData.length === 1 ? '' : 's'})</span>
<button disabled={page === totalPages || loading} onClick={() => setPage(p => p + 1)}>Next</button>
</PaginationContainer>
<FooterSpacer />
</TableContainer>
</Content>

{/* Add Column modal */}
{showAddColumn && (
  <ModalOverlay onClick={() => setShowAddColumn(false)}>
    <ModalBox onClick={(e) => e.stopPropagation()}>
      <h3>Add a column</h3>
      <ModalField>
        <label>Column name</label>
        <input
          type="text"
          placeholder="e.g. WSP Account No"
          value={newColumnLabel}
          onChange={(e) => setNewColumnLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAddColumn(); }}
        />
      </ModalField>
      <ModalActions>
        <ToolbarButton danger onClick={() => setShowAddColumn(false)}>Cancel</ToolbarButton>
        <ToolbarButton onClick={handleAddColumn}>Add</ToolbarButton>
      </ModalActions>
    </ModalBox>
  </ModalOverlay>
)}

{/* Export column picker modal */}
{showExportPicker && (
  <ModalOverlay onClick={() => setShowExportPicker(false)}>
    <ModalBox onClick={(e) => e.stopPropagation()}>
      <h3>Choose columns to export</h3>
      <PanelHeading>Standard columns</PanelHeading>
      {allColumns.filter((c) => !c.custom).map((c) => (
        <CheckboxRow key={c.key}>
          <input type="checkbox" checked={exportSelectedKeys.includes(c.key)} onChange={() => toggleExportKey(c.key)} />
          {c.label}
        </CheckboxRow>
      ))}
      {allColumns.some((c) => c.custom) && (
        <>
          <PanelHeading style={{ marginTop: 10 }}>Custom columns</PanelHeading>
          {allColumns.filter((c) => c.custom).map((c) => (
            <CheckboxRow key={c.key}>
              <input type="checkbox" checked={exportSelectedKeys.includes(c.key)} onChange={() => toggleExportKey(c.key)} />
              {c.label}
            </CheckboxRow>
          ))}
        </>
      )}
      <ModalActions>
        <ToolbarButton danger onClick={() => setShowExportPicker(false)}>Cancel</ToolbarButton>
        <ToolbarButton onClick={confirmExport}>Export {pendingExportFormat?.toUpperCase()}</ToolbarButton>
      </ModalActions>
    </ModalBox>
  </ModalOverlay>
)}

<Footer />
</Main>
  );
};

export default MeterData;